/**
 * The brand marks, drawn rather than typed (PRD v3.3 §2.3a).
 *
 * Both are inline SVG on purpose. They are small, they are needed on the first
 * paint of the shell, and an <img> would be a second request for forty bytes of
 * geometry — but the real reason is `currentColor`: the wordmark has to take
 * the colour of whatever it is sitting in, in both themes, including a hover
 * state. A file cannot do that.
 *
 * The paths are the prototype's own lettering, kept verbatim. Two things about
 * the drawing are deliberate and should survive any tidying:
 *
 *   - The ink outline is a FIXED colour, not a role. It is the line the letters
 *     are drawn with, the same way the mascot's outline is; on a dark ground it
 *     reads as weight rather than as a stroke, and letting it follow the text
 *     colour would erase it in exactly the theme that needs it most.
 *   - `paint-order="stroke fill"` puts the stroke UNDER the fill. Painted the
 *     usual way round, a 5-unit stroke eats half the letterform and "nob" turns
 *     into a smudge at 22px.
 *
 * The terminal "!" is a drawn signage device set in the brand's pink — the
 * brand book exempts it from its own no-exclamation-marks rule, because it is
 * part of the mark rather than a sentence's punctuation.
 */

const INK = "#17110b";
const PINK = "#ff59a8";
const PAPER = "#fffbed";
const CREAM = "#fff5d7";

const N =
  "M75.56 53.36Q81.56 53.36 83.9 56.48Q86.24 59.6 86.24 65.72Q86.24 67.16 86.06 68.78Q85.88 70.4 85.52 71.72L85.28 69.32Q88.04 61.52 93.92 57.14Q99.8 52.76 106.4 52.76Q114.56 52.76 119.96 57.26Q125.36 61.76 128.06 69.98Q130.76 78.2 130.76 89.36Q130.76 95 130.28 100.52Q129.8 106.04 128.96 110.54Q128.12 115.04 126.8 117.92Q124.88 122.36 121.28 124.58Q117.68 126.8 112.76 126.8Q107.24 126.8 104.3 123.14Q101.36 119.48 101.36 113Q101.36 109.4 101.72 104.48Q102.08 99.56 102.38 94.76Q102.68 89.96 102.68 86.84Q102.68 77 96.8 77Q93.56 77 91.28 80.12Q89 83.24 88.28 88.88Q87.92 91.76 87.74 96.08Q87.56 100.4 87.38 104.66Q87.2 108.92 86.96 111.8Q86.24 120.44 82.7 124.16Q79.16 127.88 73.16 127.88Q65.84 127.88 62.12 123.74Q58.4 119.6 58.4 109.76Q58.4 104.72 58.64 98.48Q58.88 92.24 59.3 85.94Q59.72 79.64 60.32 74.48Q61.52 64.64 65.12 59Q68.72 53.36 75.56 53.36Z";
const O =
  "M159.92 128.36Q149.12 128.36 141.14 124.1Q133.16 119.84 128.78 111.92Q124.4 104 124.4 93.08Q124.4 81.68 128.84 73.16Q133.28 64.64 141.38 59.84Q149.48 55.04 160.28 55.04Q171.8 55.04 180.26 59.66Q188.72 64.28 193.4 72.56Q198.08 80.84 198.08 91.88Q198.08 103.16 193.34 111.32Q188.6 119.48 180.08 123.92Q171.56 128.36 159.92 128.36ZM161.6 103.64Q166.28 103.64 169.1 100.04Q171.92 96.44 171.92 90.44Q171.92 84.44 169.1 80.84Q166.28 77.24 161.6 77.24Q156.8 77.24 153.98 80.84Q151.16 84.44 151.16 90.44Q151.16 96.44 153.98 100.04Q156.8 103.64 161.6 103.64Z";
const B =
  "M204.8 129.2Q200.72 129.2 197.9 127.04Q195.08 124.88 194 120.44Q193.04 115.76 192.38 108.26Q191.72 100.76 191.72 92.24Q191.72 87.56 191.9 81.56Q192.08 75.56 192.5 69.44Q192.92 63.32 193.4 57.98Q193.88 52.64 194.36 49.28Q195.56 42.32 198.86 38.3Q202.16 34.28 208.4 34.28Q215.12 34.28 218.24 38.54Q221.36 42.8 221.36 48.92Q221.36 54.2 220.58 61.4Q219.8 68.6 218.24 76.04L216.8 75.32Q218.12 70.16 220.88 66.02Q223.64 61.88 227.96 59.42Q232.28 56.96 238.28 56.96Q249.68 56.96 256.34 65.3Q263 73.64 263 91.28Q263 103.64 259.28 111.92Q255.56 120.2 249.08 124.34Q242.6 128.48 234.32 128.48Q226.64 128.48 221.48 124.04Q216.32 119.6 214.76 111.2L217.28 112.52Q215.84 121.16 212.78 125.18Q209.72 129.2 204.8 129.2ZM227.36 108.32Q231.2 108.32 234.02 104.48Q236.84 100.64 236.84 92.48Q236.84 86 234.62 81.92Q232.4 77.84 227.12 77.84Q222.8 77.84 220.16 81.44Q217.52 85.04 217.52 92.36Q217.52 99.32 220.22 103.82Q222.92 108.32 227.36 108.32Z";
const BANG =
  "M265.7 135.74Q265.7 131.72 267.72 129.22Q269.73 126.72 272.96 125.5Q276.2 124.28 279.73 124.28Q285.83 124.28 289.07 127.08Q292.3 129.89 292.3 134.65Q292.3 140.5 288.27 143.12Q284.25 145.75 278.51 145.75Q272.29 145.75 269 143Q265.7 140.26 265.7 135.74ZM280.34 53.76Q287.18 53.76 290.04 58.7Q292.91 63.64 292.91 72.79Q292.91 81.33 291.87 89.87Q290.84 98.41 289.13 104.51Q287.3 111.22 284.31 114.09Q281.32 116.96 277.42 116.96Q271.44 116.96 269.06 108.29Q266.68 99.63 266.68 80.11Q266.68 70.35 267.84 64.62Q269 58.88 271.99 56.32Q274.98 53.76 280.34 53.76Z";

/**
 * The blob the plated mark is printed on: a hand-cut oval, drawn as a path
 * rather than as a `border-radius` so it can be stroked, offset and tilted with
 * the letters as one object.
 */
const PLATE =
  "M18 102C16 44 72 8 174 6C276 4 340 40 344 102C348 162 292 194 174 196C62 198 20 160 18 102Z";

/**
 * The wordmark, in two registers.
 *
 * PLAIN is the shell's: the letters take their colour from the container, so
 * the hover states keep working exactly as they did when this was the word
 * "nob" set in mono — the parent still owns the colour, the mark just stopped
 * being type.
 *
 * PLATED is the poster's, and it is a different drawing rather than the same
 * one made bigger. The letters go cream, they sit on a paper blob with a heavy
 * ink edge, the blob casts the same hard offset shadow as everything else on a
 * printed sheet, and the whole thing tilts three degrees. It is fixed-colour on
 * purpose: a poster is printed, and the ink does not follow the reader's theme.
 * The heavier strokes at this size are not decoration — at 280px the plain
 * mark's 5-unit outline reads as a hairline.
 */
export function Wordmark({
  className = "",
  plate = false,
}: {
  className?: string;
  plate?: boolean;
}) {
  return (
    <svg
      viewBox={plate ? "0 0 372 214" : "52 32 262 118"}
      role="img"
      aria-label="nob"
      className={`block w-auto ${plate ? "" : "h-[22px]"} ${className}`}
    >
      <g transform={plate ? "rotate(-3.2 186 107)" : undefined}>
        {plate && (
          <>
            <path d={PLATE} fill={INK} transform="translate(8 10)" />
            <path d={PLATE} fill={INK} opacity="0.16" transform="translate(12 15)" />
            <path d={PLATE} fill={PAPER} stroke={INK} strokeWidth="5.5" />
          </>
        )}
        <g transform={plate ? "translate(12 14)" : undefined}>
          <g
            fill={plate ? CREAM : "currentColor"}
            stroke={INK}
            strokeWidth={plate ? 6.5 : 5}
            strokeLinejoin="round"
            strokeLinecap="round"
            paintOrder="stroke fill"
          >
            <path d={N} />
            <path d={O} />
            <path d={B} />
          </g>
          <path
            d={BANG}
            fill={PINK}
            stroke={INK}
            strokeWidth={plate ? 6 : 4.8}
            strokeLinejoin="round"
            strokeLinecap="round"
            paintOrder="stroke fill"
            transform="translate(10 0)"
          />
        </g>
      </g>
    </svg>
  );
}

/**
 * The doorman himself, at the smallest size he still reads at — the compact
 * "knob nib" register from the brand book, not the full portrait. The full one
 * carries turbulence filters, grain plates and nine moods, and none of that
 * survives being drawn at 28 pixels or justifies its bytes in a bundle with 44
 * KB of headroom.
 *
 * His colours are fixed rather than themed for the same reason a photograph is:
 * he is a character, and a doorman who changes colour with the wallpaper is a
 * different doorman.
 */
export function NobMark({ className = "" }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      role="img"
      aria-label="Nob, the doorman"
      className={`block size-7 shrink-0 ${className}`}
    >
      <ellipse cx="17.6" cy="17.7" rx="9.4" ry="9.4" fill="#090704" />
      <circle cx="15.2" cy="15.1" r="9.4" fill="#ff7c18" stroke={INK} strokeWidth="1.45" />
      <path
        d="M9.1 12.05 C11.4 11.55 13.4 13.15 16.2 13"
        fill="none"
        stroke={INK}
        strokeWidth="1.35"
        strokeLinecap="round"
      />
      <path
        d="M16.6 13.15 C18.6 13.7 20.9 13.25 22.9 12.8"
        fill="none"
        stroke={INK}
        strokeWidth="1.35"
        strokeLinecap="round"
      />
      <path
        d="M9.2 14.35 C11.2 14.2 13.6 14.35 15.55 14.35 C15.55 16.7 14.6 18.05 12.7 18.15 C10.85 18.2 9.45 16.95 9.2 14.35 Z"
        fill="#f3e7c6"
        stroke={INK}
        strokeWidth="0.95"
      />
      <path
        d="M16.35 14.35 C18.4 14.35 20.9 14.35 22.85 14.35 C22.85 16.8 21.85 18.15 19.9 18.15 C17.95 18.15 16.55 16.95 16.35 14.35 Z"
        fill="#f3e7c6"
        stroke={INK}
        strokeWidth="0.95"
      />
      <circle cx="11.15" cy="15.7" r="1.2" fill={INK} />
      <circle cx="18.35" cy="15.75" r="1.2" fill={INK} />
      <ellipse
        cx="14.55"
        cy="17.85"
        rx="1.35"
        ry="1.25"
        fill={PINK}
        stroke={INK}
        strokeWidth="0.65"
      />
      <path
        d="M11.6 20.55 Q 14.7 20.4 17.7 20.85"
        fill="none"
        stroke={INK}
        strokeWidth="1.15"
        strokeLinecap="round"
      />
    </svg>
  );
}
